import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCostCenterData } from 'store/slice/common';
import { Table, Tag, Radio, InputNumber, Alert, Modal } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import DateField from 'components/Form/dateField';
// For `.filter-section__count`, the count badge the filter drawers use. Pulled
// in directly because this screen wants the badge without the drawer around
// it, and a second badge style would be the same object drawn twice.
import 'components/Form/filterControls.css';
import './bulkIssuance.css';
import Breadcrumbs from 'components/Breadcrumbs';
import {
  getIssuanceEmployeesApi,
  createBulkIssuanceApi,
  getWarehouseStockApi,
  uniformErrorMessage,
} from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import { getGenderLabel, employeeGenderOptions } from '../../constants';
import {
  DEFAULT_DUE_WITHIN_DAYS, MAX_DUE_WITHIN_DAYS, REPLACEMENT_OPTIONS,
  describeReplacement, describeSizeConfirmation,
  fillCandidates, fillQuantityFor, lineRowsFor, mergeDuplicateLines,
  normaliseDueDays, summariseReplacement, tallyReplacement, toReplacementOptions,
} from './replacement';
import IssuanceErrorList from '../components/IssuanceErrorList';
import PositionUniformMappingModal from '../components/PositionUniformMappingModal';
import { UNIFORM_LANE, truncatedText } from '../../components/columns';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

// Employee (CompanyUser.gender) values, not the uniform-ITEM M/F/U vocabulary
// — IssuanceEmployeesView filters CompanyUser.gender directly, so sending 'M'/
// 'F' here silently matched zero employees.
const genderChoices = employeeGenderOptions.map((g) => ({ label: g.label, value: g.value }));

/** Key an issuing-warehouse stock row / requested-quantity aggregate / grid
 *  row the same way the backend's stock rows are keyed, so availability
 *  lookups agree on identity regardless of a null (sizeless) size. */
const stockKey = (uniformItem, size) => `${uniformItem}-${size ?? 'none'}`;

/** Key for an employee's parent (group) row. Deliberately prefixed so it can
 *  never collide with a child row's `${company_user}-${uniform_item}` key, nor
 *  with the `${company_user}-empty` sentinel — the child keys are what every
 *  per-row map is keyed by, and they are left exactly as they were. */
const groupKey = (companyUser) => `emp-${companyUser}`;

/** Ceiling the employees endpoint puts on one CSV filter field (`cost_center`
 *  and `position` each accept a single id or up to 200 comma-separated). Named
 *  here so the select-all case that can breach it is caught in the field. */
const MAX_FILTER_IDS = 200;

const SORT_OPTIONS = [
  { label: 'Employee name', value: 'name' },
  { label: 'Position', value: 'position' },
  { label: 'Gender', value: 'gender' },
  { label: 'Cost center', value: 'cost_center' },
];

const strCmp = (a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });

/** Truncation for the read-only identity columns. Applied to the inner element
 *  rather than via antd's `ellipsis: true` because these columns render custom
 *  markup: `ellipsis` styles the cell, which does nothing when the cell's child
 *  is a block or flex container that sizes itself to its own content. Every use
 *  carries a `title` so the full value is still reachable on hover. */
const ELLIPSIS = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

/** CASA-first bulk issuance spreadsheet: pick one or more CASAs (required, +
 *  optional position/gender narrowing) to load every eligible employee with
 *  their position-mapped items. One batch can span several CASAs: the server
 *  checks each employee belongs to one of the submitted `cost_centers`, stamps
 *  the batch header CASA only when they all share one, and snapshots the real
 *  CASA on every line either way. The grid nests those items under one parent row per
 *  employee; employees with no mapped items still appear, as a parent row that
 *  says so and has nothing to expand (spec §32). Rows are sized and quantified
 *  inline — a quantity above zero is what puts a row in the issuance, there is
 *  no tick step — then submitted as one multi-employee bulk issuance from the
 *  chosen issuing warehouse, with a validate_only preview step before
 *  committing.
 *
 *  The flat one-row-per-(employee, item) shape survives internally as `rows`
 *  and is still what every per-row map is keyed by; grouping is a display
 *  transform over it, so no state was re-keyed. */
const BulkIssuance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Hook form, not static Modal.confirm: the static call renders outside the
  // React tree and so never sees the app's ConfigProvider theme (index.jsx),
  // rendering unthemed.
  const [modal, modalContextHolder] = Modal.useModal();
  const { issuingWarehouseOptions, defaultIssuingWarehouseOption, warehousesLoading, warehousesError, fetchWarehouses,
    uniformPositionOptions, uniformPositionsLoading } = useUniformMgmt();
  const dispatch = useDispatch();
  const { costCenter, costCenterLoading, costCenterError, userData, userDataLoading } = useSelector((state) => state.common);

  /** Memoised, which matters more than it used to. These were rebuilt on every
   *  render while they fed single-selects that close on choosing. They now feed
   *  tick menus that STAY open across many clicks, and react-select tracks the
   *  focused option by object identity — so a fresh array of fresh option
   *  objects on every render (and every tick is a render) drops the keyboard
   *  focus ring mid-selection and defeats the menu's own memoisation. */
  const casaOptions = useMemo(
    // `code` is not shown — the label is the name — but FilterSelect's
    // search matches every string field on an option, so typing a store
    // code finds its cost centre.
    () => (costCenter || []).map((c) => ({ label: c.name || c.code, value: c.id, code: c.code || '' })),
    [costCenter]
  );
  // Positions a uniform is mapped to, not every position in the company: the
  // rest can only ever return an empty grid here. Masters keeps the full list,
  // since an unmapped position is exactly what you pick when creating a mapping.
  const positionOptions = uniformPositionOptions;

  const [warehouse, setWarehouse] = useState(null);

  // CASA and Position are multi-selects sent to the endpoint as CSV; Gender is
  // still one value. All three are SCOPE — they decide which employees are
  // fetched — as opposed to the Employees/Uniforms pickers below, which only
  // narrow what is displayed out of what was already fetched.
  //
  // Scope is held TWICE, draft and applied, and the split is the whole point:
  //
  //   - the draft is what the three pickers bind to. Editing it stages a
  //     change and fires nothing.
  //   - the applied scope is what `fetchEmployees` reads, what the
  //     quantity-clearing effect keys off, and what is submitted.
  //
  // Before this the pickers WERE the applied scope, so every tick in a
  // multi-select was its own round trip: ticking six cost centres fired six
  // requests, five of them for a scope the user was still assembling, and the
  // quantity-reset effect ran five times on the way. Same draft-vs-applied
  // shape `ReportFilterBar` uses for the report tabs, so the two read alike.
  //
  // The Employees and Uniforms pickers below are deliberately NOT part of
  // this: they filter rows already in hand, so they cost nothing and stay
  // live.
  const [draftCasas, setDraftCasas] = useState([]);
  const [draftPositionFilter, setDraftPositionFilter] = useState([]);
  const [draftGender, setDraftGender] = useState('');
  const [casas, setCasas] = useState([]);
  const [positionFilter, setPositionFilter] = useState([]);
  const [gender, setGender] = useState('');
  // Replacement-due scope: which statuses to load (empty = all) and how far
  // ahead "due soon" looks. Both are sent with the fetch — the server does
  // the narrowing, so a "who is overdue" load is only the overdue rows — and
  // so both go through Apply like the other scope fields. The window is
  // sent even without a status filter, because it also decides which pill
  // each loaded row shows.
  const [draftDue, setDraftDue] = useState([]);
  const [draftDueDays, setDraftDueDays] = useState(DEFAULT_DUE_WITHIN_DAYS);
  const [due, setDue] = useState([]);
  const [dueDays, setDueDays] = useState(DEFAULT_DUE_WITHIN_DAYS);
  // "Set on all": one quantity written across every visible row. Held
  // separately from the grid's own per-row quantities — it is an input to
  // an action, not part of the issuance until the button is pressed.
  const [bulkQty, setBulkQty] = useState(null);
  const [issuanceDate, setIssuanceDate] = useState('');
  const [notes, setNotes] = useState('');

  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  const [stockMap, setStockMap] = useState({});
  const [stockLoading, setStockLoading] = useState(false);

  const [sizeMap, setSizeMap] = useState({});
  // A row is part of the issuance purely by carrying a quantity above zero —
  // there is no separate tick step (mirrors TransferFormModal and the single
  // issuance form). Rows left at zero are simply not issued.
  const [qtyMap, setQtyMap] = useState({});
  // Multi-select of uniform items, applied client-side over the already-loaded
  // rows. Empty = all, as before. There is no backend param for this and the
  // full per-employee item list is fetched regardless, so a server round trip
  // would buy nothing.
  const [itemFilter, setItemFilter] = useState([]);
  // Multi-select of employees, applied client-side over the already-loaded rows
  // exactly like the uniform filter — and for the same reason: the endpoint has
  // already narrowed the fetch to this CASA/position/gender scope, so choosing
  // five of the employees it returned is a view decision, not a new request.
  // Empty = all. This replaced the free-text search box, whose whole job
  // (match on name or employee ID) the picker's own dropdown search does, over
  // a labelled list rather than a blind text field.
  // Employees are part of the fetched scope: the ids go to the endpoint, so
  // the grid only ever holds the people picked (empty = everyone in scope).
  const [draftEmployees, setDraftEmployees] = useState([]);
  const [employeeScope, setEmployeeScope] = useState([]);
  // Whether the "positions with no uniforms" notice is expanded. Collapsed by
  // default — with one action button per position it could run longer than the
  // grid it was warning about. Deliberately NOT reset by the scope-change
  // effect below: a user who opened it once is working through it, and having
  // it snap shut on every refetch (including the one the mapping dialog fires
  // on save) would close it exactly when they were about to use it again.
  const [noticeOpen, setNoticeOpen] = useState(false);
  // Which employee groups are expanded. Collapsed by default: the complaint
  // this grouping answers is that the flat grid showed every uniform mapped to
  // every employee at once.
  const [expandedKeys, setExpandedKeys] = useState([]);
  // Employee (parent-row) ordering: 'name' | 'position' | 'gender', and an
  // antd-shaped direction so the Employee column's arrow reads it directly.
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('ascend');
  const [rowFieldErrors, setRowFieldErrors] = useState({});
  const [serverErrors, setServerErrors] = useState([]);
  const [result, setResult] = useState(null); // { kind: 'stock' | 'issued', ... }

  const [validating, setValidating] = useState(false);
  const [issuing, setIssuing] = useState(false);

  // The position whose uniform mappings are being edited inline, or null when
  // the dialog is closed. Position-scoped, never employee-scoped — several
  // employees in this grid can share one unmapped position.
  const [mappingPosition, setMappingPosition] = useState(null);

  // "Latest request wins" guards, keyed by a monotonically increasing id
  // rather than an effect-local `ignore` flag, so fetchStock/fetchEmployees
  // stay safe to call a second time from the post-issue refresh below (a
  // plain `ignore` closure only protects one call site).
  const stockReqIdRef = useRef(0);
  const employeesReqIdRef = useRef(0);
  // Set right before a post-issue refetch so the rows-reset effect below
  // (keyed on `rows`) doesn't wipe the just-set "Issuance Complete" result
  // card the moment the refreshed employee list lands.
  const suppressRowsResetRef = useRef(false);
  // Set right before the refetch that follows an inline position mapping: that
  // refetch adds rows to a grid the user is part-way through filling in, so
  // their existing size/qty/selection is carried across instead of being wiped
  // by the very action meant to unblock them.
  const preserveGridOnNextRowsRef = useRef(false);
  // Set alongside it: what the inline mapping just created, so the refetch can
  // say so when none of it reaches anyone in the current list. Cleared on
  // consumption, on every scope change, and whenever the CASA is cleared —
  // a superseded fetch returns at the reqId guard *before* reaching the
  // consumption point, so without those extra clears the next successful
  // fetch for a different scope would consume it and blame that scope for a
  // mapping it was never about.
  const pendingMappingCheckRef = useRef(null);

  /** At least one CASA is what makes this screen live: the endpoint requires
   *  `cost_center` (its only other entry point was the free-text `search`,
   *  which this screen no longer sends), and loading every employee in the
   *  company would not be a sane default anyway. */
  const hasCasa = casas.length > 0;

  /** The endpoint accepts a comma-separated list of at most 200 ids per field.
   *  Select-all on a tenant with more CASAs or positions than that can exceed
   *  it in one click, so it is checked here and reported in the field rather
   *  than left to come back as an opaque 400. */
  /** Named individually, because either field can breach on its own and a
   *  message that blames both sends the user to narrow a picker that was never
   *  the problem.
   *
   *  Measured on the DRAFT: the warning has to appear as the user ticks past
   *  the cap, not after they apply, and Apply is what it gates. */
  const draftOverCapFields = [
    draftCasas.length > MAX_FILTER_IDS && 'cost centers',
    draftPositionFilter.length > MAX_FILTER_IDS && 'positions',
  ].filter(Boolean);
  const draftFilterIdsExceeded = draftOverCapFields.length > 0;

  /** The same test against the APPLIED scope, which is what is actually sent.
   *  Apply refuses to promote an over-cap draft, so in practice this is always
   *  false — it stays as the guard on submit and on the fetch, because those
   *  must be answerable from the scope being used rather than from whatever
   *  happens to be staged in the pickers. Reading the draft there would
   *  disable Issue over a selection the user has not applied and the grid does
   *  not reflect. */
  const overCapFields = [
    casas.length > MAX_FILTER_IDS && 'cost centers',
    positionFilter.length > MAX_FILTER_IDS && 'positions',
  ].filter(Boolean);
  const filterIdsExceeded = overCapFields.length > 0;

  /** Is there anything staged that Apply would actually change? Compared by
   *  id CONTENT, not array identity — react-select hands back a new array on
   *  every tick, so `!==` would report dirty forever and pin Apply enabled. */
  const idsOf = (list) => list.map((o) => o.value).join(',');
  const scopeDirty = idsOf(draftCasas) !== idsOf(casas)
    || idsOf(draftPositionFilter) !== idsOf(positionFilter)
    || idsOf(draftEmployees) !== idsOf(employeeScope)
    || draftGender !== gender
    || idsOf(draftDue) !== idsOf(due)
    || normaliseDueDays(draftDueDays) !== normaliseDueDays(dueDays);
  // Cost centre is what a scope needs; position and employees only narrow it
  // (none chosen = every position / everyone in those cost centres).
  const draftScopeComplete = draftCasas.length > 0;

  /** Count for the Apply badge: how many of the three scope fields the draft
   *  has set. Fields, not values — "3" for three cost centres would say less
   *  about how narrow the scope is than "1". */
  const draftScopeCount = [
    draftCasas.length > 0, draftPositionFilter.length > 0, draftEmployees.length > 0, Boolean(draftGender),
    draftDue.length > 0,
    // A non-default window narrows (or widens) what "due soon" means, so it
    // counts as a filter set; a cleared field applies as the default.
    (normaliseDueDays(draftDueDays) ?? DEFAULT_DUE_WITHIN_DAYS) !== DEFAULT_DUE_WITHIN_DAYS,
  ].filter(Boolean).length;

  /** A scope handed over by another screen (the Due Replacements report's
   *  "Re-issue"): `{ casas, positions, due, dueDays }` with casas/positions
   *  as `{ label, value }` options, so nothing here waits on the option
   *  lists to resolve ids. Applied AND drafted, so the grid loads at once
   *  and the pickers show what loaded. Consumed exactly once — the router
   *  state is cleared as it is read, so Back or a refresh cannot re-apply
   *  a scope the user has since changed. */
  useEffect(() => {
    const scope = location.state?.scope;
    if (!scope) return;
    const casaList = scope.casas || [];
    const positionList = scope.positions || [];
    const employeeList = scope.employees || [];
    // Strings ("overdue") from the report's Re-issue, or options from
    // anywhere else: resolved to the option objects the picker holds, so
    // `d.value` goes on the request and `d.label` into the copy either way.
    const dueList = toReplacementOptions(scope.due);
    const days = normaliseDueDays(scope.dueDays) ?? DEFAULT_DUE_WITHIN_DAYS;
    setDraftCasas(casaList); setCasas(casaList);
    setDraftPositionFilter(positionList); setPositionFilter(positionList);
    setDraftEmployees(employeeList); setEmployeeScope(employeeList);
    setDraftDue(dueList); setDue(dueList);
    setDraftDueDays(days); setDueDays(days);
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  /** Promote the draft. The single thing on this screen that triggers an
   *  employee fetch — everything downstream keys off these three, so setting
   *  them is the fetch. */
  const applyScope = () => {
    setCasas(draftCasas);
    setPositionFilter(draftPositionFilter);
    setEmployeeScope(draftEmployees);
    setGender(draftGender);
    setDue(draftDue);
    // A cleared field applies as the default, and the field then SHOWS the
    // default — the window in force must never be one the screen does not
    // state, since it decides every "Due soon" pill.
    const days = normaliseDueDays(draftDueDays) ?? DEFAULT_DUE_WITHIN_DAYS;
    setDueDays(days);
    setDraftDueDays(days);
  };

  /** Back to the last applied scope, not to empty: "Reset" here undoes an
   *  edit in progress, and wiping a scope the user successfully loaded (along
   *  with every quantity keyed into the grid under it) is not what the word
   *  promises. Clearing outright is what the pickers' own clear buttons plus
   *  Apply are for. */
  const resetScope = () => {
    setDraftCasas(casas);
    setDraftPositionFilter(positionFilter);
    setDraftEmployees(employeeScope);
    setDraftGender(gender);
    setDraftDue(due);
    setDraftDueDays(dueDays);
  };

  // Auto-pick the issuing warehouse when the context can name an unambiguous
  // default — the only issuing warehouse, or the only issuing one that isn't a
  // main warehouse. Also reconciles the other direction: if the picked
  // warehouse drops out of the list (deactivated / stripped of the role
  // elsewhere), clear it rather than leave a now-invalid warehouse selected
  // and submittable.
  useEffect(() => {
    if (defaultIssuingWarehouseOption) {
      setWarehouse((prev) => (
        prev && issuingWarehouseOptions.some((o) => o.value === prev.value)
          ? prev
          : defaultIssuingWarehouseOption
      ));
    } else if (warehouse && !issuingWarehouseOptions.some((o) => o.value === warehouse.value)) {
      setWarehouse(null);
    }
  }, [defaultIssuingWarehouseOption, issuingWarehouseOptions, warehouse]);

  // A validation/issue result computed against one warehouse must not survive
  // a switch to another — otherwise the green "Stock sufficient" card (or row
  // errors from a 400) from A keeps rendering, with the Issue button still
  // live, once the picker reads B. That is a false assurance the user could
  // act on rather than re-validating against the warehouse actually chosen.
  useEffect(() => {
    setResult(null);
    setServerErrors([]);
    setRowFieldErrors({});
  }, [warehouse]);

  // Stock availability is scoped to whichever warehouse is currently chosen —
  // load its full stock so per-row availability is a local lookup. Guarded by
  // stockReqIdRef ("latest request wins") so a slow response for a warehouse
  // the user has since switched away from — or a stale pre-issuance snapshot
  // once a post-issue refresh has been kicked off below — can't clobber a
  // newer one and leave stale availability on screen. `stockMap` is also
  // cleared synchronously before the fetch so the Avail column can never read
  // another warehouse's numbers while the new one is in flight.
  const fetchStock = useCallback(() => {
    // Bump the ref even on this early return so a still-in-flight response
    // from a since-cleared warehouse is invalidated exactly like the old
    // `ignore` cleanup did — otherwise it can land later and repopulate
    // stockMap for a warehouse that's no longer selected.
    // `setStockLoading(false)` for the same reason as fetchEmployees' early
    // return below: bumping the ref invalidates an in-flight response and its
    // `.finally` alike, so the flag would otherwise stick true. Clearing the
    // warehouse mid-load is the reachable path — the grid spinner and the
    // submit buttons both read it.
    if (!warehouse) { stockReqIdRef.current += 1; setStockMap({}); setStockLoading(false); return; }
    const reqId = ++stockReqIdRef.current;
    setStockLoading(true);
    setStockMap({});
    getWarehouseStockApi(warehouse.value, { page_size: 1000 }).then((response) => {
      if (reqId !== stockReqIdRef.current) return;
      const rows = response.data?.results || response.data?.data || [];
      const map = {};
      rows.forEach((r) => { map[stockKey(r.uniform_item, r.size)] = r.quantity || 0; });
      setStockMap(map);
    }).catch((e) => {
      if (reqId !== stockReqIdRef.current) return;
      setStockMap({});
      toast.error(uniformErrorMessage(e, 'Failed to load warehouse stock'), toastOpts);
    }).finally(() => {
      if (reqId === stockReqIdRef.current) setStockLoading(false);
    });
  }, [warehouse]);

  useEffect(() => { fetchStock(); }, [fetchStock]);

  // CASA (required), Position or Gender change -> reload eligible employees.
  // Guarded by employeesReqIdRef ("latest request wins") so a slower
  // in-flight request for a scope the user has since changed away from
  // can't overwrite the list for the scope now selected (a CASA-switch race
  // was already found once on this plan), and so a post-issue refresh below
  // is safe to trigger a second in-flight request without a stale response
  // clobbering it.
  const fetchEmployees = useCallback(() => {
    // Bump the ref even on these early returns — CASA is clearable, so a
    // response still in flight for the just-cleared scope must not land and
    // repopulate the grid once nothing is selected (same reasoning as
    // fetchStock's early return above). The over-the-cap case takes the same
    // exit: there is no request to make, and the previously loaded list
    // belongs to a scope that is no longer what the pickers say.
    if (casas.length === 0 || casas.length > MAX_FILTER_IDS || positionFilter.length > MAX_FILTER_IDS) {
      employeesReqIdRef.current += 1;
      pendingMappingCheckRef.current = null;
      setEmployees([]);
      // Bumping the ref invalidates any in-flight response — including its
      // `.finally`, which returns at the same guard — so the loading flag has
      // to be cleared HERE or it stays true forever. The grid's spinner is
      // driven by it, so the miss showed up as a permanent spinner over the
      // empty/over-cap state rather than as a stale list.
      setEmployeesLoading(false);
      return;
    }
    const reqId = ++employeesReqIdRef.current;
    setEmployeesLoading(true);
    // CSV, per the endpoint's contract — one request for the whole selection,
    // never a request per id.
    const params = { cost_center: casas.map((c) => c.value).join(',') };
    if (positionFilter.length > 0) params.position = positionFilter.map((p) => p.value).join(',');
    if (employeeScope.length > 0) params.employee = employeeScope.map((e) => e.value).join(',');
    if (gender) params.gender = gender;
    if (due.length > 0) params.due = due.map((d) => d.value).join(',');
    const withinDays = normaliseDueDays(dueDays);
    if (withinDays != null && withinDays !== DEFAULT_DUE_WITHIN_DAYS) params.due_within_days = withinDays;
    getIssuanceEmployeesApi(params).then((response) => {
      if (reqId !== employeesReqIdRef.current) return;
      const rows = response.data?.data?.employees || [];
      setEmployees(rows);

      // Only ever set by handleMappingSaved, and cleared by every scope change
      // and by the no-CASA / over-the-cap early returns above, so this can't
      // attribute a stale mapping to an unrelated refetch.
      const check = pendingMappingCheckRef.current;
      pendingMappingCheckRef.current = null;
      const created = check?.createdItemIds || [];
      if (created.length > 0
        && !rows.some((e) => (e.items || []).some((it) => created.includes(it.uniform_item)))) {
        toast.warning(
          `Mapped to ${check.positionName}, but none of the added uniforms suit the gender of any employee listed here, so no new rows appeared.`,
          toastOpts
        );
      }
    }).catch((e) => {
      if (reqId !== employeesReqIdRef.current) return;
      setEmployees([]);
      toast.error(uniformErrorMessage(e, 'Failed to load employees'), toastOpts);
    }).finally(() => {
      if (reqId === employeesReqIdRef.current) setEmployeesLoading(false);
    });
  }, [casas, positionFilter, employeeScope, gender, due, dueDays]);

  // Size/qty are cleared *synchronously* here — not left to the
  // `rows`-dependent effect below, which only fires once the new employee
  // list actually resolves — so a user who switches CASA/position/gender and
  // clicks Issue before the fetch lands can never submit stale quantities
  // paired with the new scope (the Validate/Issue buttons are also gated on
  // `employeesLoading` below as a second, independent guard). Emptying qtyMap
  // is what clears the selection now that a quantity is what selects a row.
  useEffect(() => {
    setSizeMap({});
    setQtyMap({});
    // All three are derived from the outgoing scope's rows: both filters draw
    // their options from them, and the expanded keys name their employees. The
    // employee filter especially — a CASA or position change replaces the
    // entire cast, so holding on to a selection of people who may no longer be
    // in scope would silently narrow the new list to nothing.
    setItemFilter([]);
    setExpandedKeys([]);
    setRowFieldErrors({});
    setServerErrors([]);
    setResult(null);
    // Mirrors clearEmployeeSelection() on the single form: a pending mapping
    // attribution belongs to the scope it was made in. This effect runs only
    // on a real scope change (fetchEmployees' identity tracks the same three
    // values), never on the mapping refetch, which calls fetchEmployees direct.
    pendingMappingCheckRef.current = null;
    fetchEmployees();
  }, [casas, positionFilter, employeeScope, gender, due, dueDays, fetchEmployees]);

  // Flatten employees -> one row per (employee, item); an employee with no
  // mapped items still gets exactly one muted, unselectable row (spec §32).
  const rows = useMemo(() => {
    const out = [];
    employees.forEach((emp) => {
      if (!emp.items || emp.items.length === 0) {
        out.push({
          key: `${emp.company_user}-empty`,
          company_user: emp.company_user,
          name: emp.name,
          emp_no: emp.emp_no,
          position_name: emp.position_name,
          cost_center_name: emp.cost_center_name,
          gender: emp.gender,
          uniform_item: null,
          uniform_name: null,
          sizes: [],
          profile_size: null,
          profile_size_code: null,
          isEmpty: true,
        });
        return;
      }
      // One row per current issuance line of the item (its own size,
      // quantity and due date), or one row for a never-issued item. See
      // lineRowsFor for the keys and presets.
      emp.items.forEach((it) => { out.push(...lineRowsFor(emp, it)); });
    });
    return out;
  }, [employees]);

  // Whenever the underlying employee/item set changes, reset the grid: preset
  // each row's size from its profile size, leave every quantity at zero, and
  // clear any errors/result that refer to the previous set of rows.
  //
  // Quantity presets to 0, not 1. Quantity is now the only thing that puts a
  // row into the issuance, so a preset of 1 across a CASA's whole staff would
  // mean "issue one of everything to everyone" on a single click of Issue.
  //
  // Exception: the post-issue refresh in runBulk() below also changes
  // `employees` (deliberately, to pull fresh stock/profile-size numbers into
  // this same grid) and sets suppressRowsResetRef first — without that guard,
  // the refreshed employee list landing here would immediately null out the
  // "Issuance Complete" result card this effect would otherwise still clear.
  //
  // Second exception: preserveGridOnNextRowsRef (set by the inline
  // position-mapping flow) carries the user's existing per-row size/qty and
  // selection across the refetch, keeping only keys that still exist. Every
  // other `rows` change is a genuine scope change and still resets in full.
  useEffect(() => {
    const preserve = preserveGridOnNextRowsRef.current;
    preserveGridOnNextRowsRef.current = false;
    // Read, do NOT consume: this flag is cleared at the bottom of the effect.
    // The post-issue refetch sets it, and reloads the same scope purely to pull
    // fresh stock and profile sizes — so it is not a scope change and must not
    // throw away the view the user set up. Without this, issuing silently
    // cleared the uniform filter and collapsed every group while the result
    // card sat there claiming success.
    const postIssue = suppressRowsResetRef.current;
    const liveGroupKeys = new Set(
      rows.filter((r) => !r.isEmpty).map((r) => groupKey(r.company_user))
    );
    // Both filters draw their options from these rows, so a genuine scope
    // change can leave either holding items — or people — nobody in the new
    // scope has. The inline-mapping refetch (preserve) and the post-issue
    // refetch both keep them, pruned to what still exists, along with whichever
    // groups were open minus any employee who is now gone.
    if (!preserve && !postIssue) {
      setItemFilter([]);
      setExpandedKeys([]);
    } else {
      const liveItemIds = new Set(rows.filter((r) => !r.isEmpty).map((r) => r.uniform_item));
      // Not restricted to non-empty rows: an employee with no mapped items is
      // still someone the picker can name and the grid still shows them, so
      // pruning them out here would drop a live selection.
      setItemFilter((prev) => {
        const next = prev.filter((o) => liveItemIds.has(o.value));
        return next.length === prev.length ? prev : next;
      });
      setExpandedKeys((prev) => prev.filter((k) => liveGroupKeys.has(k)));
    }
    // Functional updates: reading sizeMap/qtyMap/selectedRowKeys directly here
    // would drag them into this effect's deps and re-run the whole reset on
    // every inline edit.
    setSizeMap((prev) => {
      const next = {};
      rows.forEach((r) => {
        if (r.isEmpty) return;
        // A line row presets the size that line was issued in (when the
        // item still offers it); anything else presets the profile size.
        next[r.key] = preserve && r.key in prev ? prev[r.key] : (r.preset_size ?? r.profile_size ?? null);
      });
      return next;
    });
    setQtyMap((prev) => {
      const next = {};
      rows.forEach((r) => {
        if (r.isEmpty) return;
        next[r.key] = preserve && r.key in prev ? prev[r.key] : 0;
      });
      return next;
    });
    setRowFieldErrors({});
    if (suppressRowsResetRef.current) {
      suppressRowsResetRef.current = false;
    } else {
      setServerErrors([]);
      setResult(null);
    }
  }, [rows]);

  /** The rows actually being issued: those carrying a quantity. Single source of
   *  truth for validation, for the payload and for the buttons' state.
   *  Deliberately computed from `rows`, not `filteredRows` — a quantity typed
   *  on a row that a later filter hides is still part of the issuance, which is
   *  why `hiddenEnteredCount` below surfaces exactly that case. */
  const enteredKeys = useMemo(
    () => rows.filter((r) => !r.isEmpty && (qtyMap[r.key] ?? 0) > 0).map((r) => r.key),
    [rows, qtyMap]
  );

  /** The uniform items actually present in the loaded rows — the only ones
   *  worth offering, since filtering is client-side over exactly these. */
  const itemOptions = useMemo(() => {
    const byId = new Map();
    rows.forEach((r) => {
      if (r.isEmpty || byId.has(r.uniform_item)) return;
      byId.set(r.uniform_item, { label: r.uniform_name || `#${r.uniform_item}`, value: r.uniform_item });
    });
    return [...byId.values()].sort((a, b) => strCmp(a.label, b.label));
  }, [rows]);

  /** Employees the scope picker offers: the company list narrowed to the
   *  cost centres currently drafted. This does not depend on the fetched
   *  grid — the picked ids are sent WITH the fetch — which is what makes the
   *  picker usable before the first load. The label carries the employee ID
   *  because FilterSelect's option search matches on it. */
  const employeeOptions = useMemo(() => {
    const casaIds = new Set(draftCasas.map((c) => String(c.value)));
    const positionIds = new Set(draftPositionFilter.map((p) => String(p.value)));
    const wantedGender = (draftGender || '').toLowerCase();
    return (userData || [])
      .filter((u) => u.is_active !== false && casaIds.has(String(u.cost_center)))
      // Follow the rest of the drafted scope: only people in the chosen
      // positions (and gender, when one is picked) are offered, so the picker
      // never lists someone the fetch would drop anyway. A backend that does
      // not send `position` yet leaves this to the server-side fetch rather
      // than emptying the picker.
      .filter((u) => positionIds.size === 0 || u.position === undefined || positionIds.has(String(u.position)))
      .filter((u) => !wantedGender || u.gender === undefined || String(u.gender || '').toLowerCase() === wantedGender)
      .map((u) => {
        const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || `#${u.id}`;
        // Same format as the other employee pickers: "Name (EMP123)".
        return { label: u.emp_no ? `${name} (${u.emp_no})` : name, value: u.id };
      })
      .sort((a, b) => strCmp(a.label, b.label));
  }, [userData, draftCasas, draftPositionFilter, draftGender]);

  // Narrowing the scope (fewer cost centres, positions or a gender) can leave
  // picked employees outside the list; drop them rather than send ids the
  // endpoint would filter out anyway, which would read as "nobody loaded".
  useEffect(() => {
    setDraftEmployees((prev) => {
      if (prev.length === 0) return prev;
      const offered = new Set(employeeOptions.map((o) => o.value));
      const next = prev.filter((o) => offered.has(o.value));
      return next.length === prev.length ? prev : next;
    });
  }, [employeeOptions]);

  // Both filters are non-destructive, exactly as the search already was: they
  // change which rows are DISPLAYED and never touch sizeMap/qtyMap, so a
  // quantity entered before a filter narrowed the grid survives it and is
  // still issued. That is deliberate but not self-evident, so
  // `hiddenEnteredCount` reports it next to the submit buttons.
  const filteredRows = useMemo(() => {
    const itemIds = new Set(itemFilter.map((o) => o.value));
    if (itemIds.size === 0) return rows;
    return rows.filter((r) => {
      // An employee with no mapped items has no uniform to match, so filtering
      // by uniform necessarily excludes them. The aggregate Alert above the
      // grid still accounts for them.
      if (itemIds.size > 0 && (r.isEmpty || !itemIds.has(r.uniform_item))) return false;
      return true;
    });
  }, [rows, itemFilter]);

  /** How many rows carry a quantity but are not currently displayed. They are
   *  still in the payload, so this is shown beside the submit buttons — nothing
   *  gets issued invisibly. */
  const hiddenEnteredCount = useMemo(() => {
    const visible = new Set(filteredRows.map((r) => r.key));
    return enteredKeys.filter((k) => !visible.has(k)).length;
  }, [filteredRows, enteredKeys]);

  /** One parent row per employee, that employee's item rows nested underneath
   *  as antd tree data. The child rows are the SAME row objects, carrying the
   *  same `${company_user}-${uniform_item}` key — so sizeMap, qtyMap,
   *  rowFieldErrors and the server's error correlation all map onto them
   *  unchanged. Only the dataSource shape and the columns differ. */
  const replacementSummary = useMemo(
    () => summariseReplacement(tallyReplacement(filteredRows)),
    [filteredRows]
  );

  const groupedRows = useMemo(() => {
    const byUser = new Map();
    filteredRows.forEach((r) => {
      let group = byUser.get(r.company_user);
      if (!group) {
        group = {
          key: groupKey(r.company_user),
          isGroup: true,
          company_user: r.company_user,
          name: r.name,
          emp_no: r.emp_no,
          position_name: r.position_name,
          cost_center_name: r.cost_center_name,
          gender: r.gender,
          hasItems: false,
          // Urgent items under this employee, for the collapsed parent row —
          // the pills themselves are on the children, which may be folded.
          overdue: 0,
          dueSoon: 0,
          children: [],
        };
        byUser.set(r.company_user, group);
      }
      // The `-empty` sentinel contributes no child row: the parent renders the
      // "no uniforms available" note itself, so there is nothing to expand.
      if (r.isEmpty) return;
      group.hasItems = true;
      if (r.replacement_status === 'overdue') group.overdue += 1;
      else if (r.replacement_status === 'due_soon') group.dueSoon += 1;
      group.children.push(r);
    });
    return [...byUser.values()].map((g) => (
      // `children: []` would still draw an expand icon on a row with nothing
      // behind it, so an itemless employee gets no children key at all.
      g.hasItems ? g : { ...g, children: undefined }
    ));
  }, [filteredRows]);

  /** Employee ordering. Grouping collapsed Position and Gender into the parent
   *  row, which took their column `sorter`s with them — and the Employee
   *  column's own name sorter along with it. antd has no way to offer several
   *  sort keys from one column header (its multi-`sorter` support is for
   *  sorting BY several columns at once, not for choosing between keys), so the
   *  two lost keys live in the select above the grid while name stays on the
   *  column header. Both write this one piece of state, so the header arrow and
   *  the select can never disagree about what the grid is ordered by. */
  const sortedGroups = useMemo(() => {
    const dir = sortDir === 'descend' ? -1 : 1;
    const valueOf = (g) => {
      if (sortBy === 'position') return g.position_name;
      if (sortBy === 'cost_center') return g.cost_center_name;
      // Sorted by the label actually rendered, not the raw code, so the order
      // matches what the user reads.
      if (sortBy === 'gender') return getGenderLabel(g.gender);
      return g.name;
    };
    return [...groupedRows].sort((a, b) => dir * strCmp(valueOf(a), valueOf(b)));
  }, [groupedRows, sortBy, sortDir]);

  const expandableGroupKeys = useMemo(
    () => groupedRows.filter((g) => g.hasItems).map((g) => g.key),
    [groupedRows]
  );

  /** Per-employee tallies for the parent row: how many of that employee's items
   *  carry a quantity, how many units that is, and how many item rows there are
   *  to fill in.
   *
   *  Computed from `filteredRows` — the same population the parent's `children`
   *  come from — so every number on a parent row describes the rows nested
   *  under it. It used to count the unfiltered `rows`, which made a filtered
   *  group read "2 of 7" above three visible children.
   *
   *  Scoping only the denominator would have been worse than either: the
   *  numerator counts rows carrying a quantity, filters here are
   *  non-destructive, and a quantity typed before a filter narrowed the grid
   *  survives it — so an unfiltered numerator over a visible denominator can
   *  read "2 of 1". All three move together for that reason.
   *
   *  Nothing is lost by not counting hidden quantities here: they are still in
   *  the payload, and `hiddenEnteredCount` reports them beside the submit
   *  buttons and again in the confirm dialog. This badge answers "what is on
   *  this employee's rows in front of me"; that warning answers "what else is
   *  going out". */
  const groupTotals = useMemo(() => {
    const totals = {};
    filteredRows.forEach((r) => {
      if (r.isEmpty) return;
      const key = groupKey(r.company_user);
      if (!totals[key]) totals[key] = { items: 0, units: 0, total: 0 };
      totals[key].total += 1;
      const qty = qtyMap[r.key] ?? 0;
      if (qty > 0) { totals[key].items += 1; totals[key].units += qty; }
    });
    return totals;
  }, [filteredRows, qtyMap]);

  // The dead ends in this grid, collapsed to the level the fix actually
  // operates at: one entry per POSITION with no issuable uniforms, not one per
  // employee — mapping is master data, and offering it per row would imply
  // otherwise. Employees with no position at all are counted separately: there
  // is nothing to map a uniform to, so no action is offered for them.
  const { unmappedPositions, noPositionCount, noticeEmployeeCount } = useMemo(() => {
    const byPosition = new Map();
    let noPosition = 0;
    employees.forEach((emp) => {
      if (emp.items && emp.items.length > 0) return;
      if (!emp.position) { noPosition += 1; return; }
      const entry = byPosition.get(emp.position);
      if (entry) entry.count += 1;
      else byPosition.set(emp.position, { id: emp.position, name: emp.position_name || 'Unnamed position', count: 1 });
    });
    const positionsList = [...byPosition.values()].sort((a, b) => strCmp(a.name, b.name));
    return {
      unmappedPositions: positionsList,
      noPositionCount: noPosition,
      // How many people the collapsed summary is actually about. The position
      // count alone ("3 positions have no uniforms") does not say whether that
      // is 3 employees or 300, which is the part that decides whether it is
      // worth expanding.
      noticeEmployeeCount: positionsList.reduce((n, p) => n + p.count, 0),
    };
  }, [employees]);

  // Same reasoning as the single form: emptying the CASA picker disables the
  // Position select without emptying it, so a leftover selection would silently
  // narrow the next CASAs picked. Changing which CASAs are ticked keeps it —
  // the control stays visible and enabled.
  const handleCasaChange = (vals) => {
    const next = vals || [];
    setDraftCasas(next);
    if (next.length === 0) setDraftPositionFilter([]);
  };

  // Fired by the inline mapping dialog whenever at least one mapping was
  // created — including a partial failure, where the dialog stays open on what
  // failed while this grid already reflects what landed.
  const handleMappingSaved = ({ createdItemIds, position: mappedPosition }) => {
    preserveGridOnNextRowsRef.current = true;
    // Checked once the refetch lands: a mapping can be created correctly and
    // still add no row, because the API only lists items whose gender suits
    // the employee. Without this the success toast fires, the grid and the
    // notice are unchanged, and nothing explains why.
    pendingMappingCheckRef.current = {
      createdItemIds,
      positionName: mappedPosition?.name || 'the position',
    };
    fetchEmployees();
  };

  const clearRowError = (rowKey) => {
    setRowFieldErrors((prev) => {
      if (!(rowKey in prev)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  /** What the issuing warehouse holds of this item in this size. 0 while the
   *  stock fetch is in flight or no warehouse is chosen; the Qty input is
   *  disabled in both those states, so zero is never read as "out of stock". */
  const availableFor = (uniformItem, sizeId) => stockMap[stockKey(uniformItem, sizeId)] ?? 0;

  const updateSize = (rowKey, uniformItem, sizeId) => {
    setSizeMap((prev) => ({ ...prev, [rowKey]: sizeId }));
    // Changing size changes the cap — without this, a quantity typed against a
    // well-stocked size survives a switch to a size that has less.
    //
    // Only ever called from the sized branch, so a null here means the user
    // CLEARED the size. Availability is unknowable then, and clamping to the 0
    // that `stockKey(item, null)` misses on would silently wipe a quantity they
    // already typed. Leave it: the row prompts to choose a size or clear the
    // quantity, its Qty input stays enabled so the second option is actually
    // reachable, and the submit blocks until one of the two happens.
    if (sizeId == null) { clearRowError(rowKey); return; }
    const available = availableFor(uniformItem, sizeId);
    setQtyMap((prev) => (
      (prev[rowKey] ?? 0) <= available ? prev : { ...prev, [rowKey]: available }
    ));
    clearRowError(rowKey);
  };

  /** The visible rows "Fill due rows" would touch: in the applied
   *  Replacement scope (overdue + due soon when none is applied), currently
   *  at zero. Over `filteredRows`, so the Uniforms filter narrows it too —
   *  it acts on what the operator can see. */
  const fillTargets = useMemo(
    () => fillCandidates(filteredRows, due, qtyMap),
    [filteredRows, due, qtyMap]
  );

  /** Item rows on screen — what "all rows" counts. The `-empty` sentinel is
   *  not one: it carries no uniform, so no quantity can land on it. */
  const visibleItemRowCount = useMemo(
    () => filteredRows.filter((r) => !r.isEmpty).length,
    [filteredRows]
  );

  /** One click, a quantity on every target row: the units its due lines
   *  hold (one for the never-issued), capped by the stock of the size
   *  chosen, exactly as typing would be. Rows it cannot fill are counted
   *  and named in the toast rather than silently left at zero. */
  /** Write a quantity across a set of rows, capped by the stock of each
   *  row's chosen size exactly as typing into the cell would be. Rows with
   *  no size chosen cannot be capped, so they are skipped rather than
   *  guessed at; every skip is counted and named in the toast, so a fill
   *  that did less than it looked like it would never passes unremarked.
   *
   *  `quantityFor` decides what each row gets — the row's own due units for
   *  "Fill due rows", one fixed number for "Set on all". */
  const writeQuantities = (targets, quantityFor, verb) => {
    const next = {};
    let filled = 0; let capped = 0; let noSize = 0; let noStock = 0;
    targets.forEach((row) => {
      const sizeId = sizeMap[row.key] ?? null;
      if ((row.sizes || []).length > 0 && sizeId == null) { noSize += 1; return; }
      const wanted = quantityFor(row);
      const available = availableFor(row.uniform_item, sizeId);
      if (available <= 0) { noStock += 1; return; }
      next[row.key] = Math.min(wanted, available);
      if (available < wanted) capped += 1;
      filled += 1;
    });
    if (filled > 0) {
      setQtyMap((prev) => ({ ...prev, ...next }));
      setRowFieldErrors((prev) => {
        const out = { ...prev };
        Object.keys(next).forEach((k) => { delete out[k]; });
        return out;
      });
    }
    const skipped = [
      capped > 0 && `${capped} capped by stock`,
      noSize > 0 && `${noSize} skipped: no size chosen`,
      noStock > 0 && `${noStock} skipped: no stock in this size`,
    ].filter(Boolean).join(', ');
    const summary = `${verb} ${filled} row${filled === 1 ? '' : 's'}${skipped ? ` — ${skipped}` : ''}.`;
    (filled > 0 ? toast.success : toast.warning)(summary, toastOpts);
  };

  const fillDueRows = () => writeQuantities(fillTargets, fillQuantityFor, 'Filled');

  /** Every visible item row, whatever its status or current quantity —
   *  "set this on all of them" means all of them, so unlike the due fill
   *  this one deliberately overwrites. The button label says the count and
   *  the value before it is pressed, and Reset still undoes the scope. */
  const setQtyOnAllRows = () => {
    const value = Math.max(1, Math.floor(Number(bulkQty) || 0));
    writeQuantities(filteredRows.filter((r) => !r.isEmpty), () => value, `Set ${value} on`);
  };

  const updateQty = (rowKey, uniformItem, qty) => {
    // Clamped here rather than left to antd's own `max` handling: InputNumber
    // reports out-of-range values through onChange as they are typed or pasted
    // and only corrects them on blur.
    const available = availableFor(uniformItem, sizeMap[rowKey] ?? null);
    const next = Math.max(0, Math.min(Math.floor(qty || 0), available));
    setQtyMap((prev) => ({ ...prev, [rowKey]: next }));
    clearRowError(rowKey);
  };

  /** Open the groups for these employees, keeping whatever is already open. Used
   *  wherever something needs the user's attention on a child row — a failed
   *  client-side check, or a server row error from a 400 — since that row is
   *  invisible while its parent is collapsed. */
  const expandGroupsFor = (companyUsers) => {
    setExpandedKeys((prev) => [...new Set([...prev, ...companyUsers.map(groupKey)])]);
  };


  const handleItemFilterChange = (vals) => {
    const next = vals || [];
    setItemFilter(next);
    // Narrowing to specific uniforms is done in order to act on them, so open
    // the matching groups rather than leaving the user to expand each one.
    // Clearing the filter returns to the collapsed default.
    if (next.length === 0) { setExpandedKeys([]); return; }
    const ids = new Set(next.map((o) => o.value));
    setExpandedKeys([...new Set(
      rows.filter((r) => !r.isEmpty && ids.has(r.uniform_item)).map((r) => groupKey(r.company_user))
    )]);
  };

  // Requested quantity aggregated per (uniform_item, size) across every row
  // carrying a quantity — a client-side hint for the Avail column; the server
  // remains authoritative on actual stock sufficiency. Note that each row's own
  // `max` caps it at the full availability, so several employees can each ask
  // for an allowed amount and still exceed the total between them; that is what
  // this aggregate turns red for, and what Validate exists to confirm.
  const requestedByKey = useMemo(() => {
    const map = {};
    enteredKeys.forEach((key) => {
      const row = rows.find((r) => r.key === key);
      if (!row || row.isEmpty) return;
      const sizeVal = sizeMap[key] ?? null;
      const qty = qtyMap[key] ?? 0;
      const k = stockKey(row.uniform_item, sizeVal);
      map[k] = (map[k] || 0) + qty;
    });
    return map;
  }, [enteredKeys, rows, sizeMap, qtyMap]);

  const getAvail = (row) => {
    if (row.isEmpty) return 0;
    const sizeVal = sizeMap[row.key] ?? null;
    return stockMap[stockKey(row.uniform_item, sizeVal)] ?? 0;
  };

  const missingSizeCount = useMemo(() => (
    enteredKeys.reduce((count, key) => {
      const row = rows.find((r) => r.key === key);
      if (!row || row.isEmpty) return count;
      const hasSizes = (row.sizes || []).length > 0;
      const sizeVal = sizeMap[key] ?? null;
      return hasSizes && !sizeVal ? count + 1 : count;
    }, 0)
  ), [enteredKeys, rows, sizeMap]);

  const buildEmployeesPayload = () => {
    const byUser = {};
    const order = [];
    enteredKeys.forEach((key) => {
      const row = rows.find((r) => r.key === key);
      if (!row || row.isEmpty) return;
      const size = sizeMap[key] ?? null;
      const qty = qtyMap[key] ?? 0;
      if (!byUser[row.company_user]) { byUser[row.company_user] = []; order.push(row.company_user); }
      byUser[row.company_user].push({ uniform_item: row.uniform_item, size, quantity: qty });
    });
    // Per-line rows re-issued in the same size collapse into one request
    // line; the server rejects a duplicate (item, size) pair otherwise.
    return order.map((companyUser) => ({
      company_user: companyUser, lines: mergeDuplicateLines(byUser[companyUser]),
    }));
  };

  const validateForm = () => {
    if (issuingWarehouseOptions.length === 0 && !warehousesLoading) {
      toast.error(
        warehousesError ? "Couldn't load warehouses — retry" : 'No issuing warehouse configured — flag one under Masters → Warehouses',
        toastOpts
      );
      return false;
    }
    if (!warehouse) { toast.error('Issue from warehouse is required', toastOpts); return false; }
    // Availability is read from `stockMap`, which is emptied synchronously
    // before every warehouse fetch. Validating against it mid-load would fail
    // the first entered row with "Only 0 available" — a statement about the
    // fetch, not the stock. The buttons are disabled in this state too; this is
    // the backstop for a submit already in flight when the reload starts.
    if (stockLoading) { toast.error('Still loading stock for this warehouse — try again in a moment', toastOpts); return false; }
    if (!hasCasa) { toast.error('Choose one or more cost centers', toastOpts); return false; }
    if (filterIdsExceeded) { toast.error(`Too many ${overCapFields.join(' and ')} selected — at most ${MAX_FILTER_IDS} each`, toastOpts); return false; }
    if (!issuanceDate) { toast.error('Issuance date is required', toastOpts); return false; }
    if (enteredKeys.length === 0) { toast.error('Enter a quantity on at least one row', toastOpts); return false; }
    // A row carrying a quantity but missing a required size blocks the whole
    // submit rather than being dropped from the payload: the user asked for
    // that item, and silently issuing everything except it is the one outcome
    // they cannot see happening. Its parent is expanded so the flagged row is
    // actually reachable — it may well be inside a collapsed group.
    for (const key of enteredKeys) {
      const row = rows.find((r) => r.key === key);
      if (!row || row.isEmpty) continue;
      const hasSizes = (row.sizes || []).length > 0;
      if (hasSizes && !sizeMap[key]) {
        expandGroupsFor([row.company_user]);
        toast.error(`Choose a size for ${row.name} — ${row.uniform_name}, or clear its quantity`, toastOpts);
        return false;
      }
      const available = availableFor(row.uniform_item, sizeMap[key] ?? null);
      if ((qtyMap[key] ?? 0) > available) {
        expandGroupsFor([row.company_user]);
        toast.error(`Only ${available} of ${row.uniform_name} available in this size`, toastOpts);
        return false;
      }
    }
    return true;
  };

  const runBulk = (validateOnly) => {
    if (!validateForm()) return;
    const employeesPayload = buildEmployeesPayload();
    const setLoadingFn = validateOnly ? setValidating : setIssuing;
    setLoadingFn(true);
    setServerErrors([]);
    setRowFieldErrors({});
    // The CASAs the grid was loaded from, not the CASAs of the employees being
    // issued to. The server checks each employee's CASA is a member of this set
    // and, when they do not all share one, records the batch header CASA as
    // null and leans on the per-line CASA snapshots instead. Sending the loaded
    // scope rather than a set derived from the payload keeps that check
    // meaningful: it stays a statement about what the user chose to work in.
    const costCenters = casas.map((c) => c.value);
    createBulkIssuanceApi({
      warehouse: warehouse.value,
      cost_centers: costCenters,
      issuance_date: issuanceDate,
      notes: notes || '',
      validate_only: validateOnly,
      employees: employeesPayload,
    }).then((response) => {
      setLoadingFn(false);
      const data = response.data?.data || {};
      if (validateOnly) {
        setResult({ kind: 'stock', ...data });
        toast[data.valid ? 'success' : 'warning'](
          data.valid ? 'Stock is sufficient — ready to issue' : 'Some lines are short on stock', toastOpts
        );
      } else {
        // The submitted scope is recorded ON the result, not read live off the
        // pickers when the card renders: the card outlives the submission and
        // the user is free to change the filters underneath it, at which point
        // a live read would relabel a finished batch.
        setResult({
          kind: 'issued',
          ...data,
          submittedCasaCount: costCenters.length,
          submittedCasaLabel: casas.length === 1 ? casas[0].label : null,
        });
        // Quantities ARE the selection now, so clearing them is what stops the
        // just-issued batch being submitted a second time. The refetch below
        // resets them again anyway; this closes the window before it lands.
        setQtyMap({});
        toast.success(data.batch?.reference ? `Issued ${data.batch.reference}` : 'Issuance created', toastOpts);
        // Refresh both stock and employees so the Avail column and the
        // written-back profile sizes reflect this batch — otherwise a second
        // batch for the same CASA is pre-flighted against pre-issuance
        // numbers (stale Avail / shortage hints).
        suppressRowsResetRef.current = true;
        fetchStock();
        fetchEmployees();
      }
    }).catch((err) => {
      setLoadingFn(false);
      const rows_ = err.response?.data?.errors;
      if (Array.isArray(rows_) && rows_.length) {
        setServerErrors(rows_);
        // Matched on (company_user, uniform_item, size): an item can be
        // several rows here (one per issuance line), and the submit merged
        // those sharing a size into one request line, so the error lands
        // on every entered row that fed that line. Size-less errors (no
        // size chosen) fall back to every entered row of the item.
        const nextFieldErrors = {};
        rows_.forEach((r) => {
          rows.forEach((row) => {
            if (row.isEmpty || row.company_user !== r.company_user || row.uniform_item !== r.uniform_item) return;
            if (!((qtyMap[row.key] ?? 0) > 0)) return;
            const rowSize = sizeMap[row.key] ?? null;
            if (r.size == null || rowSize === r.size) nextFieldErrors[row.key] = r.error;
          });
        });
        setRowFieldErrors(nextFieldErrors);
        // Those keys address child rows, which are hidden inside collapsed
        // groups — an error rendered where nobody can see it is no error at
        // all, so open every parent a flagged row belongs to.
        expandGroupsFor(rows_.map((r) => r.company_user));
      }
      // Keep the grid and every entered quantity exactly as submitted (spec
      // §40) so the user can clear the quantity on the flagged rows and retry
      // rather than starting over.
      toast.error(uniformErrorMessage(err, validateOnly ? 'Validation failed' : 'Failed to create issuance'), toastOpts);
    });
  };

  const handleIssueClick = () => {
    if (!validateForm()) return;
    const employeesPayload = buildEmployeesPayload();
    const itemCount = employeesPayload.reduce((sum, e) => sum + e.lines.reduce((s, l) => s + (l.quantity || 0), 0), 0);
    const empCount = employeesPayload.length;
    modal.confirm({
      title: 'Confirm Issuance',
      centered: true,
      content: `Issue ${itemCount} items to ${empCount} employees from ${warehouse?.label || 'the selected warehouse'}?`
        // The count above already includes them; this is the last point at
        // which the user can find out that it does.
        + (hiddenEnteredCount > 0
          ? ` This includes ${hiddenEnteredCount} row(s) currently hidden by the employee or uniform filter.`
          : ''),
      okText: 'Issue',
      onOk: () => runBulk(false),
    });
  };

  // Parent (employee) rows carry no per-column data of their own — every
  // editable column below belongs to a child row — so their first cell spans
  // the whole table and the rest collapse. That is also what lets the employee
  // line hold name, ID, position and gender without any of them needing a
  // column of their own: four more columns is exactly what made this grid
  // scroll at every width.
  const groupSpan = (row) => (row.isGroup ? { colSpan: 6 } : {});
  const childOnly = (row) => (row.isGroup ? { colSpan: 0 } : {});

  const columns = [
    {
      title: 'Employee / Uniform',
      key: 'name',
      width: 260,
      onCell: groupSpan,
      // `sorter: true` marks the header sortable WITHOUT letting antd sort the
      // dataSource itself — a local sorter function would also reorder each
      // parent's children, which are uniform rows that have nothing to do with
      // employee name. `sortedGroups` does the ordering; this is the control
      // surface, and `sortOrder` clears when the select picks another key.
      sorter: true,
      sortOrder: sortBy === 'name' ? sortDir : null,
      showSorterTooltip: { title: 'Sort employees by name' },
      render: (_, row) => {
        if (!row.isGroup) {
          return (
            <div style={{ minWidth: 0 }}>
              <span className="d-block" style={ELLIPSIS} title={row.uniform_name || undefined}>
                {row.uniform_name}
              </span>
              {/* Which issuance this row re-issues: "L × 5 · issued
                  2026-06-01". An item held in three sizes is three rows. */}
              {row.line_label && (
                <span className="d-block text-muted" style={{ fontSize: 11.5, ...ELLIPSIS }} title={row.line_label}>
                  {row.line_label}
                </span>
              )}
            </div>
          );
        }
        // Cost centre last: a batch can span several, and the operator
        // reads it to know which store's stock is walking out the door.
        const meta = [row.emp_no, row.position_name, getGenderLabel(row.gender), row.cost_center_name]
          .filter(Boolean).join(' · ');
        const totals = groupTotals[row.key] || { items: 0, units: 0, total: 0 };
        return (
          <div className="d-flex align-items-center justify-content-between gap-3" style={{ minWidth: 0 }}>
            <div style={{ minWidth: 0 }}>
              <div title={row.name || undefined}
                style={{ fontWeight: 600, color: '#1F1F1F', ...ELLIPSIS }}>
                {row.name || '—'}
              </div>
              <div title={meta} style={{ fontSize: 12, color: '#6B7280', ...ELLIPSIS }}>
                {meta || '—'}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0, fontSize: 12 }}>
              {/* Urgent count for a folded group; the per-item pills are on
                  the children. Overdue outranks due-soon when both exist. */}
              {row.overdue > 0 ? (
                <span style={{ color: '#B42318', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {row.overdue} overdue
                </span>
              ) : row.dueSoon > 0 ? (
                <span style={{ color: '#B54708', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {row.dueSoon} due soon
                </span>
              ) : null}
              {/* "available", not "mapped": the API also drops mapped items
                  whose gender doesn't suit the employee, so an itemless
                  employee is not proof the position has no mappings at all. */}
              {!row.hasItems ? (
                <span className="text-muted fst-italic">
                  No uniforms available for {row.position_name || 'this position'}
                </span>
              ) : totals.items > 0 ? (
                <span style={{
                  color: '#D3001A', fontWeight: 600, background: 'rgba(211,0,26,0.08)',
                  borderRadius: 12, padding: '2px 10px', whiteSpace: 'nowrap',
                }}>
                  {totals.items} of {totals.total} · {totals.units} unit{totals.units === 1 ? '' : 's'}
                </span>
              ) : (
                <span className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                  {totals.total} uniform{totals.total === 1 ? '' : 's'}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Current Size',
      key: 'profile_size_code',
      width: 90,
      onCell: childOnly,
      render: (_, row) => row.profile_size_code || '—',
    },
    {
      // Read-only, from the payload: overdue / due soon / never issued / up
      // to date, with the dates behind it on hover. This is the signal the
      // Replacement scope filter loads by, shown on every row so an
      // unfiltered grid still tells the operator who needs a uniform.
      title: 'Replacement',
      key: 'replacement',
      width: 130,
      onCell: childOnly,
      render: (_, row) => {
        const d = describeReplacement(row);
        if (!d) return '—';
        return (
          <div title={d.title || undefined}>
            <span style={{
              display: 'inline-block', fontSize: 12, fontWeight: 600, lineHeight: '18px',
              borderRadius: 12, padding: '1px 8px', whiteSpace: 'nowrap', ...d.style,
            }}>
              {d.label}
            </span>
            {/* Several due lines (sizes or times) behind one pill: say so, or
                a re-issue reads as one unit when it is nine. */}
            {d.detail && (
              <div className="text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>{d.detail}</div>
            )}
          </div>
        );
      },
    },
    {
      // Not sortable: unlike the identity columns above, this reads live,
      // per-row editable state (the size the user is currently choosing).
      // Sorting on it would reorder the row out from under the user the
      // moment they change it — directly disrupting the inline edit this
      // grid exists for.
      title: 'Issue Size',
      key: 'issue_size',
      // Kept wide on purpose: the read-only identity column is capped and
      // truncates, so this is not the column that yields when space runs out.
      width: 170,
      onCell: childOnly,
      render: (_, row) => {
        const hasSizes = (row.sizes || []).length > 0;
        // "Entered", not "ticked": a quantity is what puts this row in the
        // issuance, so a quantity is what makes a missing size an error.
        const entered = (qtyMap[row.key] ?? 0) > 0;
        if (!hasSizes) {
          const err = rowFieldErrors[row.key];
          return (
            <div style={{ width: '100%', minWidth: 130 }}>
              <input className="form-control form-control-sm" value="Standard" disabled readOnly
                style={err ? { border: '1px solid #ff4d4f' } : undefined} />
              {err && <small className="text-danger d-block">{err}</small>}
            </div>
          );
        }
        const sizeOptions = row.sizes.map((s) => ({ label: `${s.name} (${s.code})`, value: s.id }));
        const currentSizeId = sizeMap[row.key] ?? null;
        const currentSizeOpt = sizeOptions.find((o) => o.value === currentSizeId) || null;
        const missing = entered && !currentSizeId;
        const err = rowFieldErrors[row.key];
        const selectId = `bulk-issue-size-${row.key}`;
        const missingMsgId = `${selectId}-missing`;
        const errorMsgId = `${selectId}-error`;
        // The <label> now carries only the accessible NAME. The error text used
        // to be appended to it, because FilterSelect passed no aria-* through;
        // it takes `aria-describedby`/`aria-invalid` now, so the errors are
        // announced as a description pointing at the very <small> elements the
        // sighted user reads. Same words, same DOM nodes, one source — and the
        // name no longer changes as the row goes in and out of error.
        const selectLabel = `Issue size for ${row.uniform_name}, ${row.name}`;
        // The two messages below are mutually exclusive (`!missing && err`), so
        // this describes whichever one is actually on screen.
        const describedBy = missing ? missingMsgId : (err ? errorMsgId : undefined);
        const confirmation = describeSizeConfirmation(row);
        // Announced as part of the select's description, not as a bare
        // coloured pill: a sighted user learns the prefill's provenance from
        // the badge, and a screen reader user has to learn it from somewhere.
        const confirmationMsgId = confirmation ? `${selectId}-confirmation` : undefined;
        return (
          <div style={{ width: '100%', minWidth: 130 }}>
            <label className="visually-hidden" htmlFor={selectId}>{selectLabel}</label>
            <div style={missing ? { border: '1px solid #ff4d4f', borderRadius: 6 } : undefined}>
              <FilterSelect
                inputId={selectId}
                aria-describedby={[describedBy, confirmationMsgId].filter(Boolean).join(' ') || undefined}
                aria-invalid={missing || err ? true : undefined}
                isClearable
                onChange={(opt) => updateSize(row.key, row.uniform_item, opt?.value ?? null)}
                optionLabel="label" optionValue="value"
                options={sizeOptions} value={currentSizeOpt}
                placeholder="Select size"
                noOptionsLabel="sizes"
              />
            </div>
            {/* Not "Size is required": the quantity is the other half of the
                problem and often the half the user would rather remove, so the
                message names both ways out — and the Qty input stays enabled
                for exactly that reason. */}
            {missing && <small id={missingMsgId} className="text-danger d-block">Choose a size or clear this quantity</small>}
            {!missing && err && <small id={errorMsgId} className="text-danger d-block">{err}</small>}
            {confirmation && (
              <small
                id={confirmationMsgId}
                className={`d-block bulk-size-confirmation bulk-size-${confirmation.tone}`}
                title={confirmation.title}
              >
                {confirmation.text}
              </small>
            )}
          </div>
        );
      },
    },
    {
      // Not sortable — same reason as Issue Size: this is live editable state.
      title: 'Qty',
      key: 'qty',
      width: 95,
      onCell: childOnly,
      render: (_, row) => {
        const available = getAvail(row);
        const qty = qtyMap[row.key] ?? 0;
        const hasSizes = (row.sizes || []).length > 0;
        // Only the last of these is actually about this item's stock. stockMap
        // is empty while loading and with no warehouse, and is keyed per
        // (item, size) — so a sized row with no size chosen misses it too.
        // Reporting any of those three as "out of stock" would be a lie.
        let blockedReason = null;
        if (!warehouse) blockedReason = 'No warehouse';
        else if (stockLoading) blockedReason = 'Checking…';
        else if (hasSizes && (sizeMap[row.key] ?? null) == null) blockedReason = 'Pick a size';
        else if (available <= 0) blockedReason = 'None in stock';

        // Never strand a quantity behind a disabled input: clearing a size, or
        // stock dropping to 0 under an entered row, otherwise blocks the submit
        // with a value the user cannot reach to remove. A row carrying a
        // quantity stays editable whenever the block is one they must resolve
        // themselves, so it can always be zeroed. The transient states stay
        // disabled — the value is preserved and submit is blocked anyway.
        const transient = !warehouse || stockLoading;
        const disabled = !!blockedReason && (transient || qty === 0);
        const helpId = `bulk-qty-help-${row.key}`;
        return (
          <div style={{ minWidth: 0 }}>
            <InputNumber
              min={0}
              // The cap the backend would enforce anyway, applied where the
              // user can see it; updateQty re-clamps on top of it. Floored at
              // the current quantity so antd's blur-correction cannot silently
              // wipe a stranded value before the user has decided about it.
              max={Math.max(available, qty)}
              // Blank rather than 0: an empty box reads as "not issuing this",
              // which is exactly what a zero quantity now means.
              value={qty > 0 ? qty : null}
              placeholder="0"
              disabled={disabled}
              aria-label={`Quantity to issue of ${row.uniform_name} for ${row.name}`}
              aria-invalid={qty > available || undefined}
              aria-describedby={blockedReason ? helpId : undefined}
              onChange={(val) => updateQty(row.key, row.uniform_item, val)}
              style={{ width: '100%', minWidth: 56 }}
            />
            {blockedReason && (
              <small id={helpId}
                className={disabled ? 'text-muted d-block' : 'text-danger d-block'}
                style={{ fontSize: 11 }}>
                {/* An enabled row showing a reason is a stranded one: say what
                    to do about it rather than restating the state. */}
                {disabled ? blockedReason : 'Clear this quantity'}
              </small>
            )}
          </div>
        );
      },
    },
    {
      // Not sortable — its value depends on the row's live Issue Size/Qty
      // choices, so it would reorder rows mid-edit just like those columns.
      title: 'Avail',
      key: 'avail',
      width: 65,
      onCell: childOnly,
      render: (_, row) => {
        const sizeVal = sizeMap[row.key] ?? null;
        const available = getAvail(row);
        // Each row is individually capped at `available`, so this can only go
        // red when several rows want the same item/size and together exceed it.
        const requested = requestedByKey[stockKey(row.uniform_item, sizeVal)] ?? 0;
        const exceeds = requested > available;
        return (
          <span className={exceeds ? 'text-danger fw-semibold' : ''}
            title={exceeds ? `${requested} requested across this grid, ${available} available` : undefined}>
            {available}
          </span>
        );
      },
    },
  ];

  const stockColumns = [
    { title: 'Uniform', dataIndex: 'uniform_item_name', ...truncatedText(UNIFORM_LANE) },
    { title: 'Size', render: (_, r) => r.size_code || '—' },
    { title: 'Required', dataIndex: 'required' },
    {
      title: 'Available',
      render: (_, r) => (
        <span className={r.required > r.available ? 'text-danger fw-semibold' : ''}>{r.available}</span>
      ),
    },
  ];

  // Also gated on `employeesLoading`: a stale selection can't survive a CASA/
  // position/gender switch (cleared synchronously above), but while a new
  // fetch is in flight there's nothing valid to submit yet either way.
  // And on `stockLoading`: every row's availability comes from `stockMap`,
  // which is empty while a warehouse's stock is in flight, so a submit landing
  // in that window would be validated against nothing.
  const submitDisabled = !warehouse || !hasCasa || filterIdsExceeded
    || employeesLoading || stockLoading || enteredKeys.length === 0;

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {modalContextHolder}
        <Breadcrumbs maintitle="Bulk Issuance" />

        <IssuanceErrorList errors={serverErrors} />

        <div className="card mb-3">
          <div className="card-body">
            {/* Two dense rows, not four stacked ones. Scope on top (what to
                load), then the issuance's own fields (what to write). Vertical
                rhythm comes from `g-3`'s row gutter alone — the per-field
                `mb-3` that used to sit on top of it was doubling the gap on
                every wrap, which is most of what made this block "too much".
                Four columns per row, 3+3+3+3, rather than five in one: at 1280
                a col-xl-3 is ~232px of control and a col-xl-2 only ~147px, and
                three of these fields have a hard floor above that — Gender's
                three solid radio buttons need ~200px or they wrap to two lines,
                and Position/Employees carry chips ~130px wide that would
                otherwise scroll the control sideways and push "+n more" out of
                sight (multiValueLabel is deliberately clip/visible, so a chip
                does not truncate). Five fields cannot give three of them a
                col-xl-3 out of twelve columns, so Employees leads row 2 — still
                immediately after Gender in reading order. */}
            <div className="row g-3 mb-3">
              <div className="col-12 col-sm-6 col-xl-3">
                <label className="col-form-label">Issue From Warehouse <span className="text-danger">*</span></label>
                {issuingWarehouseOptions.length === 0 && !warehousesLoading ? (
                  <div className="text-danger" style={{ fontSize: 12.5, padding: '8px 0' }}>
                    {warehousesError ? (
                      <>
                        Couldn't load warehouses —{' '}
                        <button type="button" className="btn btn-link p-0 align-baseline" onClick={fetchWarehouses}>
                          retry
                        </button>.
                      </>
                    ) : (
                      'No issuing warehouse configured — flag one under Masters → Warehouses.'
                    )}
                  </div>
                ) : (
                  <FilterSelect onChange={setWarehouse} optionLabel="label" optionValue="value"
                    options={issuingWarehouseOptions} value={warehouse} placeholder="Select warehouse"
                    isLoading={warehousesLoading} noOptionsLabel="warehouses" />
                )}
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                <label className="col-form-label" htmlFor="bulk-casa-filter">Cost Center <span className="text-danger">*</span></label>
                {/* Same in-field treatment as the warehouse field above: CASA is
                    required and gates Position, the employee picker, the item picker
                    and the whole grid, so if its list failed to load the screen is
                    dead until it is retried. */}
                {costCenterError && !costCenterLoading ? (
                  <div className="text-danger" style={{ fontSize: 12.5, padding: '8px 0' }}>
                    Couldn't load cost centers —{' '}
                    <button type="button" className="btn btn-link p-0 align-baseline"
                      onClick={() => dispatch(fetchCostCenterData())}>
                      retry
                    </button>.
                  </div>
                ) : (
                  /* `isClearable` restored. Removing it was wrong: I argued an
                     empty CASA is "a state that is never valid", but empty is
                     precisely how this page LOADS, and the empty state below
                     exists for it. Without the clear, the only way back from a
                     select-all over 40 CASAs was to reopen the menu and click
                     select-all again to invert it — correct, but undiscoverable
                     from behind a "+n more" summary. Submission is still
                     blocked while empty by validateForm and submitDisabled, so
                     the clear resets scope rather than permitting a bad
                     issuance. */
                  <FilterSelect inputId="bulk-casa-filter" isMulti checkboxMenu selectAll isClearable
                    maxVisibleValues={1}
                    onChange={handleCasaChange} optionLabel="label" optionValue="value"
                    options={casaOptions} value={draftCasas} placeholder="Select Cost Center(s)" isLoading={costCenterLoading}
                    noOptionsLabel="cost centers" />
                )}
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                <label className="col-form-label" htmlFor="bulk-position-filter">Position</label>
                {/* No selection cap here: "all positions" via select-all is the
                    normal case. The page-level MAX_FILTER_IDS check still guards
                    the endpoint's own limit. */}
                <FilterSelect inputId="bulk-position-filter" isMulti checkboxMenu selectAll isClearable
                  maxSelections={0}
                  maxVisibleValues={1}
                  onChange={(vals) => setDraftPositionFilter(vals || [])} optionLabel="label" optionValue="value"
                  options={positionOptions} value={draftPositionFilter} placeholder="Select position(s)" isLoading={uniformPositionsLoading}
                  isDisabled={draftCasas.length === 0} noOptionsLabel="positions" />
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                {/* A radio group is not labelled by a neighbouring <label>: there
                    is no single form control for `htmlFor` to point at, so
                    without this the group is anonymous to a screen reader and
                    each button announces only its own word ("Male"). antd's
                    Radio.Group forwards `role` and every `aria-*` to its wrapper
                    (rc-util `pickAttrs({aria:true})`), so both land on the div
                    that actually contains the radios. */}
                <label className="col-form-label d-block" id="bulk-gender-label">Gender</label>
                <Radio.Group value={draftGender} onChange={(e) => setDraftGender(e.target.value)} optionType="button" buttonStyle="solid"
                  role="radiogroup" aria-labelledby="bulk-gender-label">
                  {genderChoices.map((g) => (
                    <Radio.Button key={g.value || 'all'} value={g.value}>{g.label}</Radio.Button>
                  ))}
                </Radio.Group>
              </div>
            </div>
            <div className="row g-3">
              <div className="col-12 col-sm-6 col-xl-3">
                <label className="col-form-label" htmlFor="bulk-employee-filter">Employees</label>
                {/* Part of the scope: the picked ids are sent to the endpoint, so
                    the grid loads only these people. Empty = everyone in the
                    cost centres above. Needs Apply like the other scope fields. */}
                <FilterSelect
                  inputId="bulk-employee-filter"
                  isMulti checkboxMenu selectAll isClearable
                  maxVisibleValues={1}
                  onChange={(vals) => setDraftEmployees(vals || [])}
                  optionLabel="label" optionValue="value"
                  options={employeeOptions} value={draftEmployees}
                  placeholder={draftCasas.length === 0 ? 'Choose cost center first' : 'All employees in scope'}
                  isLoading={userDataLoading}
                  isDisabled={draftCasas.length === 0}
                  noOptionsLabel="employees"
                />
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                <label className="col-form-label" htmlFor="bulk-due-filter">Replacement</label>
                {/* Part of the scope, unlike the Uniforms filter beside it: the
                    server narrows the fetch to the matching item rows, so
                    "Overdue" loads only what needs issuing rather than hiding
                    the rest of a cost centre in the browser. Needs Apply. */}
                <FilterSelect
                  inputId="bulk-due-filter"
                  isMulti checkboxMenu isClearable
                  maxVisibleValues={2}
                  onChange={(vals) => setDraftDue(vals || [])}
                  optionLabel="label" optionValue="value"
                  options={REPLACEMENT_OPTIONS} value={draftDue}
                  placeholder={draftCasas.length === 0 ? 'Choose cost center first' : 'Any status'}
                  isDisabled={draftCasas.length === 0}
                  noOptionsLabel="statuses"
                />
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                <label className="col-form-label" htmlFor="bulk-due-days">Due within (days)</label>
                {/* Sets how far ahead "Due soon" looks — both for the filter
                    and for the pill on every loaded row. 30 is the server
                    default and the reports dashboard's window. */}
                <InputNumber
                  id="bulk-due-days"
                  min={0} max={MAX_DUE_WITHIN_DAYS} step={1} precision={0}
                  value={draftDueDays}
                  onChange={(v) => setDraftDueDays(v)}
                  disabled={draftCasas.length === 0}
                  style={{ width: '100%' }}
                  aria-describedby="bulk-due-days-hint"
                />
                <small id="bulk-due-days-hint" className="text-muted">
                  "Due soon" = replacement due today or within this many days.
                </small>
              </div>
            </div>
            {/* The endpoint takes at most 200 ids per CSV field, and select-all
                on a large tenant can exceed that in one click. Said in the
                block rather than left to come back as a 400 on submit — and the
                fetch is skipped meanwhile, so the grid never shows a list that
                does not match the pickers. */}
            {draftFilterIdsExceeded && (
              <small className="text-danger d-block mt-2">
                Too many {draftOverCapFields.join(' and ')} selected — at most {MAX_FILTER_IDS} each.
              </small>
            )}

            {/* Scope is staged, not live: the three pickers above cost a
                request each time they change, and with multi-selects that was
                one request per tick. Apply is the only thing that loads
                employees. Reset returns the pickers to the scope currently
                loaded, which is why it is disabled in lockstep with Apply —
                with nothing staged there is nothing to undo.

                Apply is deliberately NOT gated on the Cost Center picker being
                non-empty. Clearing it is a real intention — "stop showing me
                this" — and applying it means exactly that: `fetchEmployees`
                already treats an empty `casas` as "no request, empty the
                grid", and `hasCasa` already renders "Choose one or more cost
                centers to load employees." Gating it instead left the primary
                action dead underneath a banner telling the user to press it,
                with Reset the only way out. The only thing that disables Apply
                now is a draft the server would reject (over the id cap), and
                that says so in the line directly above. */}
            <div className="d-flex align-items-center flex-wrap gap-2 mt-3">
              <button type="button" className="btn btn-primary btn-sm"
                disabled={!scopeDirty || !draftScopeComplete || draftFilterIdsExceeded}
                onClick={applyScope}>
                <i className="fa fa-filter" style={{ marginRight: 6 }} aria-hidden="true" />
                Apply
                {draftScopeCount > 0 && (
                  <>
                    <span className="filter-section__count ms-2" aria-hidden="true">{draftScopeCount}</span>
                    {/* The badge is decorative; the count belongs in the
                        button's accessible name so it is announced with the
                        control rather than as a stray number after it. */}
                    <span className="visually-hidden">
                      , {draftScopeCount} scope {draftScopeCount === 1 ? 'filter' : 'filters'} set
                    </span>
                  </>
                )}
              </button>
              <Button label="Reset" type="button" className="btn btn-link p-0 ms-1"
                disabled={!scopeDirty}
                style={{ color: scopeDirty ? 'var(--brand-text-muted)' : '#d1d5db', fontWeight: 600 }}
                onClick={resetScope} />
              {/* Said out loud rather than left to the Apply button's enabled
                  state: the grid below still shows the previous scope, and
                  nothing else on screen distinguishes "these are the rows for
                  what the pickers say" from "these are stale".

                  Three wordings, because one sentence cannot be true in all
                  three states — and a banner that tells the user to press a
                  button which is disabled is worse than no banner. Over the
                  cap, Apply IS unavailable and the line says why rather than
                  pointing at it; with the cost centres cleared, Apply works
                  but does not "reload" anything, so it promises what actually
                  happens. */}
              {scopeDirty && (
                <small className={draftFilterIdsExceeded ? 'text-danger' : 'text-muted'} role="status">
                  {draftFilterIdsExceeded
                    ? 'Scope changed, but Apply is unavailable until the selections above are within the limit.'
                    : draftCasas.length === 0
                      ? 'Cost Center cleared — Apply to empty the grid.'
                      : 'Scope changed — Apply to reload employees.'}
                </small>
              )}
            </div>

            {hasCasa && !filterIdsExceeded && !employeesLoading && employees.length === 0 && (
              <small className="text-warning d-block mt-2">
                {/* The Replacement scope is the likeliest empty result — on a
                    fresh tenant nobody is overdue — so it gets its own words
                    rather than a list of filters the operator did not touch. */}
                {due.length > 0
                  ? `No employees in this scope have a uniform that is ${due.map((d) => d.label.toLowerCase()).join(' or ')}.`
                  : 'No eligible employees for the selected cost center/position/gender.'}
              </small>
            )}
          </div>
        </div>

        {missingSizeCount > 0 && (
          <Alert type="warning" showIcon className="mb-3"
            message={`${missingSizeCount} row${missingSizeCount === 1 ? '' : 's'} with a quantity still need${missingSizeCount === 1 ? 's' : ''} a size`} />
        )}

        {/* One notice for the whole grid, with one action per unmapped
            position — not a link on each empty employee row, which would read
            as "give this person a uniform" when the mapping is company-wide
            master data. */}
        {!employeesLoading && (unmappedPositions.length > 0 || noPositionCount > 0) && (
          <Alert
            type="warning"
            showIcon
            className="mb-3"
            message={
              <span className="d-flex align-items-center flex-wrap gap-2">
                <span>
                  {unmappedPositions.length > 0
                    ? `${unmappedPositions.length} position${unmappedPositions.length === 1 ? ' has' : 's have'} no uniforms available for ${noticeEmployeeCount} employee${noticeEmployeeCount === 1 ? '' : 's'}`
                    : `${noPositionCount} employee${noPositionCount === 1 ? ' has' : 's have'} no position assigned`}
                </span>
                {/* A disclosure, not a second Alert action: the summary line
                    is the whole message when collapsed, and one position per
                    button made this card taller than the grid it was warning
                    about on a tenant with many positions. */}
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 align-baseline"
                  style={{ fontWeight: 600 }}
                  aria-expanded={noticeOpen}
                  aria-controls="bulk-unmapped-detail"
                  onClick={() => setNoticeOpen((open) => !open)}
                >
                  <i className={`fa fa-chevron-${noticeOpen ? 'up' : 'down'} me-1`} aria-hidden="true"
                    style={{ fontSize: 10 }} />
                  {noticeOpen ? 'Hide' : 'Expand'}
                </button>
              </span>
            }
            description={
              // Rendered while collapsed with `hidden` rather than unmounted:
              // `aria-controls` above has to point at an element that exists,
              // and `hidden` keeps the collapsed body out of the tab order so
              // the per-position buttons are not reachable while invisible.
              <div id="bulk-unmapped-detail" hidden={!noticeOpen}>
                {unmappedPositions.length > 0 && (
                  <>
                    <div className="mb-2">
                      Nothing can be issued to those employees until their position has uniforms
                      mapped to it. Mapping is company master data — it applies to every employee
                      holding the position, not only the ones listed here.
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {unmappedPositions.map((p) => (
                        <Button
                          key={p.id}
                          type="button"
                          icon="fa fa-plus"
                          className="btn btn-outline-primary btn-sm"
                          label={`Assign uniforms to ${p.name} (${p.count} employee${p.count === 1 ? '' : 's'} here)`}
                          onClick={() => setMappingPosition({ id: p.id, name: p.name })}
                        />
                      ))}
                    </div>
                  </>
                )}
                {/* When there are no unmapped positions this Alert's `message`
                    already states the count, so the note only adds the remedy
                    rather than repeating the sentence back. */}
                {noPositionCount > 0 && (unmappedPositions.length > 0 ? (
                  <div className="text-muted mt-2" style={{ fontSize: 12.5 }}>
                    {noPositionCount} employee{noPositionCount === 1 ? ' has' : 's have'} no position at
                    all, so no uniform can be mapped to them — set a position under Employees first.
                  </div>
                ) : (
                  <div>
                    A uniform is mapped to a position, so nothing can be mapped to an employee who has
                    none — set their position under Employees first.
                  </div>
                ))}
              </div>
            }
          />
        )}

        {!hasCasa ? (
          <div className="text-muted py-5 text-center">Choose one or more cost centers to load employees.</div>
        ) : (
          <>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <div className="text-muted" style={{ fontSize: 12.5 }}>
              {groupedRows.length} employee{groupedRows.length === 1 ? '' : 's'}
              {itemFilter.length > 0 && ' shown'}
              {/* Over the rows in front of the user, filters included, so it
                  describes the grid and not the cost centre. */}
              {replacementSummary && (
                <span role="status"> · {replacementSummary}</span>
              )}
            </div>
            <div className="d-flex align-items-center flex-wrap gap-2">
              {/* Uniforms lives HERE, not in the scope block above: it narrows
                  the rows already loaded (the endpoint has no item parameter)
                  and needs no Apply, so beside Cost Center and Position it
                  read as a scope picker that was mysteriously disabled until
                  the first load. Next to Sort by, it sits with the other
                  controls that act on the grid in front of the user. */}
              <label className="text-muted mb-0" htmlFor="bulk-uniform-filter" style={{ fontSize: 12.5 }}>Uniforms</label>
              <div style={{ minWidth: 220 }}>
                <FilterSelect
                  inputId="bulk-uniform-filter"
                  isMulti
                  checkboxMenu
                  selectAll
                  maxVisibleValues={2}
                  menuPosition="fixed"
                  onChange={handleItemFilterChange}
                  optionLabel="label" optionValue="value"
                  options={itemOptions} value={itemFilter}
                  placeholder={itemOptions.length === 0 ? 'No uniforms loaded' : 'All uniforms'}
                  isDisabled={itemOptions.length === 0}
                  noOptionsLabel="uniforms"
                />
              </div>
              {/* Position and gender lost their column headers when they moved
                  onto the employee row, so their sorting lives here. Name is
                  here too, kept in step with the column arrow, so there is one
                  obvious place to change the order rather than two. */}
              <label className="text-muted mb-0" htmlFor="bulk-sort-by" style={{ fontSize: 12.5 }}>Sort by</label>
              <div style={{ minWidth: 180 }}>
                <FilterSelect inputId="bulk-sort-by" isClearable={false} menuPosition="fixed"
                  onChange={(opt) => setSortBy(opt?.value || 'name')} optionLabel="label" optionValue="value"
                  options={SORT_OPTIONS} value={SORT_OPTIONS.find((o) => o.value === sortBy) || SORT_OPTIONS[0]}
                  placeholder="Sort by" noOptionsLabel="sort options" />
              </div>
              <Button type="button"
                label={sortDir === 'ascend' ? 'A–Z' : 'Z–A'}
                icon={sortDir === 'ascend' ? 'fa fa-arrow-down-a-z' : 'fa fa-arrow-up-z-a'}
                className="btn btn-outline-secondary btn-sm"
                title={sortDir === 'ascend' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
                onClick={() => setSortDir((d) => (d === 'ascend' ? 'descend' : 'ascend'))} />
              {/* The count is in the label so the click is never a surprise:
                  "Fill 12 due rows" says exactly how many quantities land.
                  Disabled while stock is loading — the cap it applies is
                  not known yet — and when there is nothing left to fill. */}
              <Button type="button"
                label={`Fill ${fillTargets.length} ${due.length > 0 ? 'row' : 'due row'}${fillTargets.length === 1 ? '' : 's'}`}
                icon="fa fa-wand-magic-sparkles"
                className="btn btn-outline-primary btn-sm"
                title={due.length > 0
                  ? 'Set a quantity on every visible row in the Replacement scope that has none'
                  : 'Set a quantity on every visible overdue or due-soon row that has none'}
                disabled={fillTargets.length === 0 || stockLoading || !warehouse}
                onClick={fillDueRows} />
              {/* The other half of "fill the grid": one number on every
                  visible row, whatever its status. Deliberately overwrites —
                  that is what "set on all" means — so the count and the
                  value are both in the button's label before it is pressed. */}
              <label className="visually-hidden" htmlFor="bulk-set-all-qty">Quantity to set on every visible row</label>
              <InputNumber
                id="bulk-set-all-qty"
                min={1} step={1} precision={0}
                value={bulkQty}
                onChange={(v) => setBulkQty(v)}
                placeholder="Qty"
                disabled={visibleItemRowCount === 0 || stockLoading || !warehouse}
                style={{ width: 78 }}
              />
              <Button type="button"
                label={`Set on all ${visibleItemRowCount} row${visibleItemRowCount === 1 ? '' : 's'}`}
                icon="fa fa-list-check"
                className="btn btn-outline-secondary btn-sm"
                title="Write this quantity on every row shown, replacing what is there"
                disabled={!(Number(bulkQty) > 0) || visibleItemRowCount === 0 || stockLoading || !warehouse}
                onClick={setQtyOnAllRows} />
              <Button type="button" label="Expand all" icon="fa fa-angles-down"
                className="btn btn-outline-secondary btn-sm"
                disabled={expandableGroupKeys.length === 0}
                onClick={() => setExpandedKeys(expandableGroupKeys)} />
              <Button type="button" label="Collapse all" icon="fa fa-angles-up"
                className="btn btn-outline-secondary btn-sm"
                disabled={expandedKeys.length === 0}
                onClick={() => setExpandedKeys([])} />
            </div>
          </div>
          <Table
            rowKey={(r) => r.key}
            columns={columns}
            dataSource={sortedGroups}
            // Clicking the Employee header writes the same sort state the
            // select above does. On the third click antd clears the sort and
            // reports `columnKey: undefined` (useSorter's generateSorterInfo
            // blanks column/order/field/columnKey once no sorter is active), so
            // the guard below returns early and the current sort simply stays
            // put. The grid is never unordered either way: `sortedGroups`
            // always applies `sortBy`/`sortDir`. The `|| 'ascend'` is only a
            // defensive default — while `columnKey` is 'name' antd always
            // reports a real order.
            onChange={(_pagination, _filters, sorter) => {
              if (sorter?.columnKey !== 'name') return;
              setSortBy('name');
              setSortDir(sorter.order || 'ascend');
            }}
            // Tree data: each parent's `children` are the very same row objects
            // the flat grid used, still keyed `${company_user}-${uniform_item}`,
            // so every per-row map and the server's error correlation address
            // them exactly as before.
            expandable={{
              expandedRowKeys: expandedKeys,
              onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
            }}
            loading={employeesLoading || stockLoading}
            pagination={false}
            size="small"
            // rc-table (es/Table.js:453-460) treats a `scroll.y` as
            // `fixHeader`, which selects 'fixed' on its own, so this grid was
            // already fixed-layout. Stated outright anyway (it short-circuits
            // at es/Table.js:447) so the widths do not quietly become advisory
            // if the sticky header is ever dropped — which now also matters for
            // the parent rows' colSpan, since those span the declared columns.
            tableLayout="fixed"
            // 680 is the five column widths. It was 1250 across nine columns
            // plus a selection gutter, which scrolled at every width: grouping
            // moved position and gender onto the employee row, the checkbox
            // column went with the tick step, and the ID column was already
            // duplicated under the name. `.page-wrapper` reserves 250px for the
            // sidebar with 24px of content padding either side, so a 1024px
            // window leaves 726px here — the grid now fits without horizontal
            // scroll, and has more room still below 992px where the sidebar
            // goes off-canvas.
            // 810 = 680 + the 130px Replacement column between Current Size
            // and Issue Size. A 1024px window with the sidebar open leaves
            // 726px, so the grid scrolls sideways there again and fits from
            // ~1110px; the due pill on every row was judged worth that.
            scroll={{ x: 810, y: 600 }}
          />
          </>
        )}

        {/* What gets recorded on every issued line — not a filter, so it lives
            with the actions rather than in the scope panel. */}
        <div className="card mt-3">
          <div className="card-body py-3">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-4">
                <label className="col-form-label" htmlFor="bulk-issuance-date">Issuance Date <span className="text-danger">*</span></label>
                <DateField id="bulk-issuance-date" value={issuanceDate} onChange={setIssuanceDate} />
              </div>
              <div className="col-12 col-md-8">
                <label className="col-form-label" htmlFor="bulk-notes">Notes</label>
                <input id="bulk-notes" className="form-control" value={notes}
                  onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end align-items-center flex-wrap gap-3 mt-3">
          {/* The filters hide rows without clearing their quantities — which is
              what stops a stray search wiping entered work — but those rows are
              still issued. Saying so here is the difference between a
              deliberate design and something going out unseen. */}
          {hiddenEnteredCount > 0 && (
            <span className="text-warning" style={{ fontSize: 12.5 }}>
              <i className="fa fa-eye-slash me-1" />
              {hiddenEnteredCount} hidden row{hiddenEnteredCount === 1 ? '' : 's'} carr
              {hiddenEnteredCount === 1 ? 'ies' : 'y'} quantities and will still be issued
            </span>
          )}
          <div className="d-flex gap-2">
            <Button label="Validate" className="btn btn-outline-primary" loading={validating}
              disabled={issuing || submitDisabled} onClick={() => runBulk(true)} />
            <Button label="Issue" className="btn btn-primary" loading={issuing}
              disabled={validating || submitDisabled} onClick={handleIssueClick} />
          </div>
        </div>

        {result?.kind === 'stock' && (
          <div className="card mt-3" style={{ overflow: 'hidden' }}>
            <div className="card-header d-flex align-items-center justify-content-between">
              <h5 className="mb-0" style={{ fontSize: 15 }}>Validation Result</h5>
              {/* The server always returns `valid: true` alongside a 200 here — any
                  stock shortage fails validate_only with a 400 instead (see
                  issuance_service.validate_issuance, which folds _stock_errors
                  into the same errors list checked before this branch), so a
                  "Shortage found" state can never actually render. Kept as a plain
                  "Stock sufficient" tag rather than a dead conditional branch. */}
              <Tag color="green">Stock sufficient</Tag>
            </div>
            <div className="card-body">
              <div className="text-muted mb-2">{result.line_count} line(s) · {result.employee_count} employee(s)</div>
              <Table columns={stockColumns} dataSource={result.stock || []}
                rowKey={(r, i) => `${r.uniform_item}-${r.size ?? 'none'}-${i}`}
                pagination={false} size="small" scroll={{ x: 'max-content' }} />
            </div>
          </div>
        )}

        {result?.kind === 'issued' && (
          <div className="card mt-3" style={{ overflow: 'hidden' }}>
            <div className="card-header d-flex align-items-center justify-content-between"
              style={{ background: '#f6ffed', borderBottom: '1px solid #b7eb8f' }}>
              <h5 className="mb-0 text-success"><i className="fa fa-check-circle me-2" />Issuance Complete</h5>
            </div>
            <div className="card-body">
              <p className="mb-2">
                Batch{' '}
                <button type="button" className="btn btn-link p-0" style={{ color: '#D3001A' }}
                  onClick={() => result.batch?.id && navigate(`/uniform-management/issuance/batches/${result.batch.id}`)}>
                  {result.batch?.reference || (result.batch?.id ? `#${result.batch.id}` : '—')}
                </button>{' '}
                created.
              </p>
              <div>{result.issued_count} item(s) issued to {result.employee_count} employee(s).</div>
              {/* The batch header CASA is null whenever the batch spans more
                  than one — the per-line CASA snapshots carry the truth in that
                  case. Saying so beats an empty field, which reads as data
                  missing rather than as a batch that deliberately has no single
                  CASA. The single-CASA fallback to the submitted label covers a
                  null arriving for any other reason. */}
              <div className="text-muted mt-1" style={{ fontSize: 12.5 }}>
                Cost Center:{' '}
                {result.batch?.cost_center_name
                  || (result.submittedCasaCount > 1 ? 'mixed cost centers' : (result.submittedCasaLabel || '—'))}
              </div>
            </div>
          </div>
        )}

        <PositionUniformMappingModal
          open={!!mappingPosition}
          position={mappingPosition}
          onClose={() => setMappingPosition(null)}
          onSaved={handleMappingSaved}
        />
      </div>
    </div>
  );
};

export default BulkIssuance;
